import React from "react";
import {
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { DeliveryOption } from "../../types";
import { CptAuditTooltipContent } from "./CptAuditTooltipContent";

export interface DeliveryOptionCptAuditInfoProps {
  option: DeliveryOption;
}

/** Marks the popover scroll container — modal wheel capture must ignore these targets. */
export const CPT_AUDIT_POPOVER_SCROLL_SELECTOR =
  '[data-cpt-audit-popover-scroll="true"]';

export const isCptAuditPopoverScrollTarget = (
  target: EventTarget | null,
): boolean =>
  target instanceof Element &&
  Boolean(target.closest(CPT_AUDIT_POPOVER_SCROLL_SELECTOR));

const containPopoverScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  event.stopPropagation();

  const container = event.currentTarget;
  const { scrollTop, scrollHeight, clientHeight } = container;
  const canScroll = scrollHeight > clientHeight + 1;

  if (!canScroll) {
    return;
  }

  const maxScrollTop = scrollHeight - clientHeight;
  container.scrollTop = Math.max(
    0,
    Math.min(scrollTop + event.deltaY, maxScrollTop),
  );
  event.preventDefault();
};

export const DeliveryOptionCptAuditInfo: React.FC<
  DeliveryOptionCptAuditInfoProps
> = ({ option }) => (
  <Popover
    trigger="hover"
    placement="right"
    openDelay={200}
    isLazy
    gutter={10}
    flip
  >
    <PopoverTrigger>
      <IconButton
        variant="unstyled"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        minW="auto"
        h="auto"
        cursor="pointer"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        aria-label="View delivery slot calculation details"
        icon={<InfoOutlineIcon color="blue.500" boxSize={3.5} />}
      />
    </PopoverTrigger>
    <Portal>
      <PopoverContent
        width="auto"
        borderColor="gray.200"
        boxShadow="md"
        zIndex={1600}
        maxH="70vh"
        overflow="hidden"
        overscrollBehavior="contain"
        onWheel={(event) => event.stopPropagation()}
        _focus={{ boxShadow: "md" }}
      >
        <PopoverBody
          px={3}
          py={1.5}
          maxH="70vh"
          overflowY="auto"
          overscrollBehavior="contain"
          data-cpt-audit-popover-scroll="true"
          onWheel={containPopoverScroll}
          onWheelCapture={containPopoverScroll}
        >
          {option.cptAudit ? (
            <CptAuditTooltipContent audit={option.cptAudit} option={option} />
          ) : null}
        </PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
);
